import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/shared/Sidebar';
import useAuthStore from '../store/authStore';
import { useTheme } from '../contexts/ThemeContext';
import financialSubmissionService, { SubmittedFinancialData, KPIData } from '../services/financialSubmissionService';
import organisationSettingsService, { OrganisationSettings } from '../services/organisationSettingsService';
import LocationGroupService from '../services/locationGroupService';
import { parseAIResponse, processMarkdown } from '../utils/aiResponseParser';
import VisualizationRenderer, { VisualizationData } from '../components/csv/VisualizationRenderer';
import ReportPopup from '../components/csv/ReportPopup';
import { useModelSelection } from '../hooks/useModelSelection';
import './ExecutiveSearchPage.css';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

interface SmartModeState {
  enabled: boolean;
  aggressiveness: 'conservative' | 'balanced' | 'aggressive';
  settings: {
    autoTimeFiltering: boolean;
    autoKPIFiltering: boolean;
    focusNonZero: boolean;
    useConversationContext: boolean;
  };
  dataReduction?: {
    originalSize: number;
    filteredSize: number;
    percentage: number;
  };
}

interface SubmissionFilter {
  locations: string[];
  years: number[];
  months: number[];
  kpiCategories: string[];
  includeAllQualityScores: boolean;
  intelligentFiltering: boolean;
}


const ExecutiveSearchPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { theme } = useTheme();
  
  // Get organization's AI model selection
  const { modelId: organizationModelId, loading: modelLoading, aiModelTier } = useModelSelection();
  
  // State
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Data state (similar to Analytics page)
  const [submissions, setSubmissions] = useState<SubmittedFinancialData[]>([]);
  const [allSubmissions, setAllSubmissions] = useState<SubmittedFinancialData[]>([]);
  const [loading, setLoading] = useState(true);
  const [organisationSettings, setOrganisationSettings] = useState<OrganisationSettings | null>(null);
  const [accessibleLocations, setAccessibleLocations] = useState<any[]>([]);
  const [locationsLoaded, setLocationsLoaded] = useState(false);
  
  // Report popup state (replaces chat interface)
  const [showReport, setShowReport] = useState(false);
  const [reportData, setReportData] = useState<string>('');
  const [reportUserQuestion, setReportUserQuestion] = useState<string>('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  // Organization logo state
  const [organizationLogo, setOrganizationLogo] = useState<string | null>(null);
  
  // Rotating placeholder text
  const placeholderTexts = [
    "Ask me a question about your data",
    "Which of my dealers performed the best?",
    "Which models is selling the best?"
  ];
  const [currentPlaceholderIndex, setCurrentPlaceholderIndex] = useState(0);
  
  // Filter state (always use intelligent filtering for executive search)
  const [submissionFilter, setSubmissionFilter] = useState<SubmissionFilter>({
    locations: [],
    years: [],
    months: [],
    kpiCategories: [],
    includeAllQualityScores: true,
    intelligentFiltering: true // Always enabled for executive search
  });
  
  // Smart Mode state (always enabled for executive search)
  const [smartMode] = useState<SmartModeState>({
    enabled: true, // Always enabled for executive search
    aggressiveness: 'balanced',
    settings: {
      autoTimeFiltering: true,
      autoKPIFiltering: true,
      focusNonZero: true,
      useConversationContext: true // Enable conversation context
    }
  });
  
  // Refs
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const submissionsLoadingRef = useRef(false);
  const locationsLoadingRef = useRef(false);

  // Report handlers
  const openReport = (content: string, userQuestion: string) => {
    setReportData(content);
    setReportUserQuestion(userQuestion);
    setShowReport(true);
  };

  const closeReport = () => {
    setShowReport(false);
    setReportData('');
    setReportUserQuestion('');
  };


  // Load organization logo
  const loadOrganizationLogo = async () => {
    if (!user) return;
    
    try {
      const settings = await organisationSettingsService.getOrganisationSettings();
      if (settings.logoUrl) {
        // Construct full URL if it's a relative path
        // logoUrl is now something like /api/organisation-settings/logo/{orgId}
        const baseUrl = settings.logoUrl.startsWith('http') 
          ? settings.logoUrl 
          : `${(process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api$/, '')}${settings.logoUrl}`;
        // Add cache-busting parameter to ensure fresh image load
        const logoUrl = `${baseUrl}?t=${Date.now()}`;
        setOrganizationLogo(logoUrl);
      } else {
        setOrganizationLogo(null);
      }
    } catch (error) {
      console.error('Error loading organization logo:', error);
      setOrganizationLogo(null);
    }
  };

  // Load organization logo on mount
  useEffect(() => {
    loadOrganizationLogo();
  }, [user]);

  // Listen for organization logo updates
  useEffect(() => {
    const handleLogoUpdate = (event: CustomEvent) => {
      console.log('Executive Search: Organization logo updated event received:', event.detail);
      try {
        loadOrganizationLogo();
        console.log('Executive Search: Logo refresh completed successfully');
      } catch (error) {
        console.error('Executive Search: Error during logo refresh:', error);
      }
    };

    window.addEventListener('organizationLogoUpdated', handleLogoUpdate as EventListener);
    
    return () => {
      window.removeEventListener('organizationLogoUpdated', handleLogoUpdate as EventListener);
    };
  }, [user]);

  // Rotate placeholder text every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPlaceholderIndex((prevIndex) => 
        (prevIndex + 1) % placeholderTexts.length
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [placeholderTexts.length]);

  // Load accessible locations (same as Analytics page)
  useEffect(() => {
    const fetchAccessibleLocations = async () => {
      if (!user || locationsLoadingRef.current || locationsLoaded) return;
      
      console.log('🔄 Executive Search: Fetching accessible locations...');
      locationsLoadingRef.current = true;
      
      try {
        const response = await fetch(`${API_BASE_URL}/user-access/accessible-locations`, {
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setAccessibleLocations(data.data);
            console.log(`📍 Executive Search: Loaded ${data.data.length} accessible locations`);
          }
        }
      } catch (error) {
        console.error('Error fetching accessible locations:', error);
        setAccessibleLocations([]);
      } finally {
        setLocationsLoaded(true);
        locationsLoadingRef.current = false;
      }
    };
    
    fetchAccessibleLocations();
  }, [user, locationsLoaded]);

  // Load submissions data using the same service as Analytics page
  useEffect(() => {
    const loadSubmissions = async () => {
      if (submissionsLoadingRef.current || !user || !locationsLoaded) {
        return;
      }
      
      submissionsLoadingRef.current = true;
      setLoading(true);
      
      try {
        // Load submissions using the service (same as Analytics page)
        const submissionsData = await financialSubmissionService.getFinancialSubmissions();
        
        // Store all submissions (including low quality ones)
        setAllSubmissions(submissionsData);
        setSubmissions(submissionsData);
        
        console.log(`📊 Executive Search: Loaded ${submissionsData.length} total submissions`);
        
        // Load organization settings
        const orgSettings = await organisationSettingsService.getOrganisationSettings();
        setOrganisationSettings(orgSettings);
        
      } catch (error) {
        console.error('Error loading submissions:', error);
        setError('Failed to load data for analysis');
      } finally {
        setLoading(false);
        submissionsLoadingRef.current = false;
      }
    };
    
    loadSubmissions();
  }, [user, locationsLoaded]);
  
  // Initialize component and hide animations
  useEffect(() => {
    // Hide background animations
    const hideAnimations = () => {
      const hudDataNodes = document.querySelector('.hud-data-nodes');
      const hudScanLines = document.querySelector('.hud-scan-lines');
      
      if (hudDataNodes) {
        (hudDataNodes as HTMLElement).style.display = 'none';
      }
      if (hudScanLines) {
        (hudScanLines as HTMLElement).style.display = 'none';
      }
      
      // Also disable body background animation
      document.body.style.setProperty('--disable-bg-animation', 'true');
      const bodyBefore = document.body;
      if (bodyBefore) {
        bodyBefore.style.setProperty('animation', 'none', 'important');
      }
    };
    
    // Show animations when component unmounts
    const showAnimations = () => {
      const hudDataNodes = document.querySelector('.hud-data-nodes');
      const hudScanLines = document.querySelector('.hud-scan-lines');
      
      if (hudDataNodes) {
        (hudDataNodes as HTMLElement).style.display = '';
      }
      if (hudScanLines) {
        (hudScanLines as HTMLElement).style.display = '';
      }
      
      document.body.style.removeProperty('--disable-bg-animation');
    };
    
    hideAnimations();
    
    // Cleanup function to restore animations when component unmounts
    return () => {
      showAnimations();
    };
  }, []);
  
  // Focus input
  useEffect(() => {
    if (inputRef.current && !loading) {
      inputRef.current.focus();
    }
  }, [loading]);

  // Helper function to get location name (same as Analytics page)
  const getLocationName = useCallback((locationId?: number): string => {
    if (!locationId) return 'Organization Level';
    const location = accessibleLocations.find(loc => loc.id === locationId);
    return location ? location.name : `Location ${locationId}`;
  }, [accessibleLocations]);
  
  // Get intelligent filter recommendations from AI (with group awareness)
  const getIntelligentFilterRecommendations = async (
    submissions: SubmittedFinancialData[],
    userQuestion: string,
    currentDataPoints: number
  ) => {
    try {
      // Extract metadata about available KPIs (for more granular intelligent filtering)
      const uniqueKPIs = new Set<string>();

      submissions.forEach(submission => {
        submission.kpiData.forEach((kpi: KPIData) => {
          uniqueKPIs.add(kpi.name);
        });
      });

      // Extract available time periods for date-aware filtering
      const availableYears = Array.from(new Set(submissions.map(s => s.year))).sort((a, b) => a - b);
      const availableMonths = Array.from(new Set(submissions.map(s => s.month))).sort((a, b) => a - b);
      const availableTimePeriods = Array.from(
        new Set(submissions.map(s => `${s.year}-${String(s.month).padStart(2, '0')}`))
      ).sort();

      // Fetch location groups for group-aware filtering
      let locationGroups: any[] = [];
      let locationGroupMap = new Map<number, string[]>();
      let groupAssignments: any[] = [];
      let availableGroups: any[] = [];
      
      if (user?.organization_id) {
        try {
          locationGroups = await LocationGroupService.getGroupsByOrganization(user.organization_id);
          console.log(`🏢 Executive Search: Loaded ${locationGroups.length} location groups for intelligent filtering`);
          
          // Build a mapping of locationId → group names
          locationGroups.forEach(group => {
            console.log(`🏢 Group "${group.group_name}" has ${group.location_ids.length} locations:`, group.location_ids);
            group.location_ids.forEach((locationId: number) => {
              if (!locationGroupMap.has(locationId)) {
                locationGroupMap.set(locationId, []);
              }
              locationGroupMap.get(locationId)!.push(group.group_name);
            });
          });
          
          console.log(`📍 Total unique locations in all groups: ${locationGroupMap.size}`);
          console.log(`📍 LocationGroupMap contents:`, Array.from(locationGroupMap.entries()).map(([id, groups]) => `${id}: [${groups.join(', ')}]`));
          
          // DEBUG: Check which locations in each group have submissions
          const submissionLocationIds = new Set(allSubmissions.map(s => s.locationId));
          console.log(`📊 Total unique location IDs in submissions: ${submissionLocationIds.size}`);
          
          locationGroups.forEach(group => {
            const locationsInGroup = group.location_ids;
            const locationsWithSubmissions = locationsInGroup.filter((id: number) => submissionLocationIds.has(id));
            const locationsWithoutSubmissions = locationsInGroup.filter((id: number) => !submissionLocationIds.has(id));
            
            console.log(`🔍 Group "${group.group_name}":`);
            console.log(`   - Locations in group: ${locationsInGroup.length} [${locationsInGroup.join(', ')}]`);
            console.log(`   - Locations WITH submissions: ${locationsWithSubmissions.length} [${locationsWithSubmissions.join(', ')}]`);
            if (locationsWithoutSubmissions.length > 0) {
              console.warn(`   ⚠️ Locations WITHOUT submissions: ${locationsWithoutSubmissions.length} [${locationsWithoutSubmissions.join(', ')}]`);
            }
          });
          
          // Create group assignment structure for AI
          groupAssignments = Array.from(locationGroupMap.entries()).map(([locationId, groupNames]) => ({
            locationId,
            locationName: getLocationName(locationId),
            groups: groupNames
          }));
          
          // Extract unique group names and descriptions
          availableGroups = locationGroups.map(group => ({
            name: group.group_name,
            description: group.group_description || '',
            locationCount: group.location_ids.length
          }));
          
          console.log(`📍 Executive Search: Built group mappings - ${groupAssignments.length} locations across ${availableGroups.length} groups`);
        } catch (error) {
          console.error('Error loading location groups for filtering:', error);
          // Continue without group data if it fails
        }
      }

      const metadata = {
        totalSubmissions: submissions.length,
        currentDataPoints: currentDataPoints,
        targetDataPoints: 50000,
        availableFilters: {
          kpiNames: Array.from(uniqueKPIs),
          locationGroups: availableGroups,
          locationGroupAssignments: groupAssignments,
          years: availableYears,
          months: availableMonths,
          timePeriods: availableTimePeriods,
          dateRange: {
            earliest: availableTimePeriods[0] || 'N/A',
            latest: availableTimePeriods[availableTimePeriods.length - 1] || 'N/A'
          }
        },
        userQuestion: userQuestion
      };

      const maxAllowedKPIs = Math.floor(50000 / submissions.length);
      console.log('📤 Executive Search: Sending filter recommendation request to AI with', Array.from(uniqueKPIs).length, 'unique KPI names');
      console.log('🎯 Executive Search: Maximum KPIs AI can recommend:', maxAllowedKPIs, `(50,000 ÷ ${submissions.length} submissions)`);
      console.log('🎯 Executive Search: Sample KPIs being analyzed:', Array.from(uniqueKPIs).slice(0, 10).join(', ') + (Array.from(uniqueKPIs).length > 10 ? `... and ${Array.from(uniqueKPIs).length - 10} more` : ''));
      console.log('🏢 Executive Search: Available groups:', availableGroups.map(g => `${g.name} (${g.locationCount} locations)`).join(', '));
      console.log('📅 Executive Search: Available date range:', availableTimePeriods[0], 'to', availableTimePeriods[availableTimePeriods.length - 1]);

      const response = await fetch(`${API_BASE_URL}/bedrock/get-filter-recommendations`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(metadata),
      });

      if (!response.ok) {
        throw new Error('Failed to get filter recommendations from AI');
      }

      const data = await response.json();
      console.log('✅ Executive Search: Received AI filter recommendations:', data.recommendations);
      
      // Return recommendations along with the location groups data for filtering
      return {
        ...data.recommendations,
        _locationGroups: locationGroups, // Pass groups data for later filtering
        _locationGroupMap: locationGroupMap // Pass map for later filtering
      };
    } catch (error: any) {
      console.error('❌ Executive Search: Error getting filter recommendations:', error);
      
      // Log more details for debugging
      if (error.message) {
        console.error('Error details:', error.message);
      }
      if (error instanceof TypeError) {
        console.error('Network or parsing error occurred');
      }
      
      return null; // Returning null will trigger the error in handleSendMessage
    }
  };
  
  
  // Create CSV content from submissions
  const createCSVContent = useCallback(async (filteredSubmissions?: SubmittedFinancialData[]) => {
    const submissionsToUse = filteredSubmissions || allSubmissions;
    
    if (!submissionsToUse.length) {
      return { csvContent: '', totalDataPoints: 0 };
    }
    
    // Get unique KPI identifiers (including time period type abbreviation)
    const kpiIdentifiers = new Set<string>();
    submissionsToUse.forEach(submission => {
      submission.kpiData.forEach((kpi: KPIData) => {
        // Include type abbreviation to distinguish between time periods (MTD, YTD, R3, etc.)
        const abbreviation = kpi.abbreviation || 'MTD'; // Default to MTD if not specified
        const identifier = `${kpi.category}_${kpi.name}_${abbreviation}`.replace(/[^a-zA-Z0-9_]/g, '_');
        kpiIdentifiers.add(identifier);
        
        // Debug logging for first few KPIs
        if (kpiIdentifiers.size <= 5) {
          console.log('🔍 Executive Search KPI:', {
            category: kpi.category,
            name: kpi.name,
            abbreviation: kpi.abbreviation,
            identifier: identifier
          });
        }
      });
    });
    
    console.log('📊 Executive Search: Total unique KPI identifiers:', kpiIdentifiers.size);
    console.log('📋 Executive Search: Sample KPI headers:', Array.from(kpiIdentifiers).slice(0, 5));
    
    // Create headers (excluding submission_id, submission_date, quality_score to save tokens)
    const headers = [
      'location_name',
      'year',
      'month',
      'month_name',
      'total_kpis',
      ...Array.from(kpiIdentifiers)
    ];
    
    // Create CSV rows
    const rows = submissionsToUse.map(submission => {
      const row: any = {
        location_name: getLocationName(submission.locationId),
        year: submission.year,
        month: submission.month,
        month_name: new Date(submission.year, submission.month - 1).toLocaleString('default', { month: 'long' }),
        total_kpis: submission.kpiData.length
      };
      
      // Add KPI values (matching using category + name + abbreviation)
      Array.from(kpiIdentifiers).forEach(identifier => {
        const kpi = submission.kpiData.find((k: KPIData) => {
          const abbreviation = k.abbreviation || 'MTD';
          const kpiIdentifier = `${k.category}_${k.name}_${abbreviation}`.replace(/[^a-zA-Z0-9_]/g, '_');
          return kpiIdentifier === identifier;
        });
        row[identifier] = kpi ? kpi.value : '';
      });
      
      return row;
    });
    
    // Convert to CSV string
    const csvContent = [
      headers.join(','),
      ...rows.map(row => headers.map(header => {
        const value = row[header];
        return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
      }).join(','))
    ].join('\n');
    
    const totalDataPoints = submissionsToUse.reduce((sum, submission) => sum + submission.kpiData.length, 0);
    
    // Log CSV preview for debugging
    console.log('📄 Executive Search CSV Preview:');
    console.log('Headers (first 8):', headers.slice(0, 8).join(', ') + '...');
    console.log('First row:', rows[0] ? Object.entries(rows[0]).slice(0, 8).map(([k,v]) => `${k}:${v}`).join(', ') : 'no rows');
    console.log('Total rows:', rows.length);
    console.log('Total columns:', headers.length);
    console.log('CSV first 500 chars:', csvContent.substring(0, 500));
    
    return { csvContent, totalDataPoints };
  }, [allSubmissions, getLocationName]);
  
  // Send a message using direct Bedrock API (same pattern as Analytics page)
  const sendMessage = async (message: string) => {
    if (!message.trim() || loading || isAnalyzing || modelLoading) return;
    
    console.log('🚀 Executive Search: Sending message:', message);
    
    setIsAnalyzing(true);
    setError(null);
    // Don't clear the input message - keep it visible during analysis
    
    try {
      console.log('🚀 Executive Search: Starting analysis...');
      
      // Use all submissions for executive search (like Analytics page)
      const submissionsToAnalyze = allSubmissions;
      const totalDataPoints = submissionsToAnalyze.reduce((sum: number, submission: SubmittedFinancialData) => sum + submission.kpiData.length, 0);
      
      console.log(`📊 Executive Search: Analyzing ${submissionsToAnalyze.length} submissions with ${totalDataPoints.toLocaleString()} data points`);
      
      let finalSubmissions = submissionsToAnalyze;
      let filterRecommendations: any = null;
      
      // Apply intelligent filtering if dataset is large (with group-aware filtering)
      if (totalDataPoints > 50000) {
        console.log('🧠 Executive Search: Large dataset detected, applying intelligent filtering...');
        
        filterRecommendations = await getIntelligentFilterRecommendations(
          submissionsToAnalyze,
          message,
          totalDataPoints
        );
        
        if (filterRecommendations) {
          // STEP 1: Apply DATE filtering FIRST (if AI recommended specific dates)
          if (filterRecommendations.years && filterRecommendations.years.length > 0) {
            console.log(`📅 Executive Search: AI recommended filtering to years:`, filterRecommendations.years);
            
            finalSubmissions = finalSubmissions.filter(submission => 
              filterRecommendations.years.includes(submission.year)
            );
            
            console.log(`✂️ Executive Search: Year filtering applied: ${submissionsToAnalyze.length} → ${finalSubmissions.length} submissions`);
          }
          
          if (filterRecommendations.months && filterRecommendations.months.length > 0) {
            console.log(`📅 Executive Search: AI recommended filtering to months:`, filterRecommendations.months);
            
            finalSubmissions = finalSubmissions.filter(submission => 
              filterRecommendations.months.includes(submission.month)
            );
            
            console.log(`✂️ Executive Search: Month filtering applied: ${finalSubmissions.length} submissions remaining`);
          }
          
          // STEP 2: Apply group filtering SECOND (if AI recommended specific groups)
          if (filterRecommendations.locationGroups && filterRecommendations.locationGroups.length > 0) {
            console.log(`🏢 Executive Search: AI recommended filtering to specific groups:`, filterRecommendations.locationGroups);
            
            const allowedLocationIds = new Set<number>();
            const locationGroups = filterRecommendations._locationGroups || [];
            
            locationGroups.forEach((group: any) => {
              if (filterRecommendations.locationGroups.includes(group.group_name)) {
                group.location_ids.forEach((locId: number) => allowedLocationIds.add(locId));
              }
            });
            
            console.log(`📍 Executive Search: Allowed locations after group filtering: ${allowedLocationIds.size}`);
            
            finalSubmissions = finalSubmissions.filter(submission => 
              submission.locationId && allowedLocationIds.has(submission.locationId)
            );
            
            console.log(`✂️ Executive Search: Group filtering applied: ${finalSubmissions.length} submissions remaining`);
          } else {
            console.log('📍 Executive Search: No group filtering - keeping all locations');
          }
          
          // STEP 3: Apply KPI filtering THIRD (if AI recommended specific KPIs)
          if (filterRecommendations.kpiNames && filterRecommendations.kpiNames.length > 0) {
            console.log(`📊 Executive Search: AI recommended ${filterRecommendations.kpiNames.length} specific KPIs`);
            
            const recommendedKPIs = new Set(filterRecommendations.kpiNames);
            
            finalSubmissions = finalSubmissions.map(submission => ({
              ...submission,
              kpiData: submission.kpiData.filter(kpi => 
                recommendedKPIs.has(kpi.name) || recommendedKPIs.has((kpi as any).identifier || '')
              )
            })).filter(submission => submission.kpiData.length > 0);
            
            console.log(`✂️ Executive Search: KPI filtering applied`);
          }
          
          const filteredDataPoints = finalSubmissions.reduce((sum, submission) => sum + submission.kpiData.length, 0);
          const reductionPercentage = ((1 - filteredDataPoints / totalDataPoints) * 100).toFixed(1);
          console.log(`🎯 Executive Search: Combined filtering applied - ${totalDataPoints.toLocaleString()} → ${filteredDataPoints.toLocaleString()} data points (${reductionPercentage}% reduction)`);
        } else {
          // CRITICAL FIX: Stop execution when intelligent filtering fails
          console.error('❌ Executive Search: Intelligent filtering failed - stopping analysis');
          throw new Error(
            '🧠 **Intelligent Filtering Failed**\n\n' +
            'The AI was unable to generate filter recommendations for your large dataset.\n\n' +
            '**What happened?**\n' +
            '• Your dataset is too large to analyze without filtering (>50,000 data points)\n' +
            '• The intelligent filtering service encountered an error\n\n' +
            '**How to proceed:**\n' +
            '1. **Manual Filtering**: Use the filter panel to reduce your dataset\n' +
            '   • Select specific time periods (months/years)\n' +
            '   • Choose specific locations or location groups\n\n' +
            '2. **Try Again**: The intelligent filtering service may be temporarily unavailable\n\n' +
            '3. **Break Down Your Question**: Ask about specific time periods or locations\n\n' +
            'Once you narrow down your scope, your analysis will proceed successfully.'
          );
        }
      }
      
      // Build location groups mapping for CSV inclusion
      let locationGroupMap = new Map<number, string[]>();
      
      if (filterRecommendations?._locationGroupMap) {
        locationGroupMap = filterRecommendations._locationGroupMap;
        console.log('🏢 Executive Search: Reusing location group map from filtering');
      } else if (user?.organization_id) {
        try {
          const locationGroups = await LocationGroupService.getGroupsByOrganization(user.organization_id);
          locationGroups.forEach(group => {
            group.location_ids.forEach((locationId: number) => {
              if (!locationGroupMap.has(locationId)) {
                locationGroupMap.set(locationId, []);
              }
              locationGroupMap.get(locationId)!.push(group.group_name);
            });
          });
          console.log('🏢 Executive Search: Built location group map for CSV inclusion');
        } catch (error) {
          console.error('Error loading groups for CSV:', error);
        }
      }
      
      // Create CSV content with location groups
      const kpiIdentifiers = new Set<string>();
      finalSubmissions.forEach(submission => {
        submission.kpiData.forEach((kpi: KPIData) => {
          // Include abbreviation to distinguish between time periods (MTD, YTD, R3, etc.)
          const abbreviation = kpi.abbreviation || 'MTD';
          const identifier = `${kpi.category}_${kpi.name}_${abbreviation}`.replace(/[^a-zA-Z0-9_]/g, '_');
          kpiIdentifiers.add(identifier);
        });
      });
      
      const headers = [
        'location_name',
        'location_groups',
        'year',
        'month',
        'month_name',
        'total_kpis',
        ...Array.from(kpiIdentifiers)
      ];
      
      const rows = finalSubmissions.map(submission => {
        const locationGroups = submission.locationId 
          ? locationGroupMap.get(submission.locationId) || []
          : [];
        
        const row: any = {
          location_name: getLocationName(submission.locationId),
          location_groups: JSON.stringify(locationGroups),
          year: submission.year,
          month: submission.month,
          month_name: new Date(submission.year, submission.month - 1).toLocaleString('default', { month: 'long' }),
          total_kpis: submission.kpiData.length
        };
        
        Array.from(kpiIdentifiers).forEach(identifier => {
          const kpi = submission.kpiData.find((k: KPIData) => {
            const abbreviation = k.abbreviation || 'MTD';
            const kpiIdentifier = `${k.category}_${k.name}_${abbreviation}`.replace(/[^a-zA-Z0-9_]/g, '_');
            return kpiIdentifier === identifier;
          });
          row[identifier] = kpi ? kpi.value : '';
        });
        
        return row;
      });
      
      // Helper function to properly escape CSV values
      const escapeCsvValue = (value: any): string => {
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        // If value contains comma, quote, or newline, escape it
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          // Replace all quotes with double quotes and wrap in quotes
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      };
      
      const csvContent = [
        headers.join(','),
        ...rows.map(row => headers.map(header => escapeCsvValue(row[header])).join(','))
      ].join('\n');
      
      console.log(`📄 Executive Search: Generated CSV with ${rows.length} rows and ${headers.length} columns`);
      console.log('📋 Executive Search: Sample KPI headers (with abbreviations):', Array.from(kpiIdentifiers).slice(0, 5));
      console.log('📊 Executive Search: CSV first 500 chars:', csvContent.substring(0, 500));
      
      // Save CSV and get fileRef (following Analytics pattern)
      const saveResponse = await fetch(`${API_BASE_URL}/bedrock/save-filtered-data`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ content: csvContent })
      });

      if (!saveResponse.ok) {
        throw new Error('Failed to save filtered data for analysis');
      }

      const { fileRef } = await saveResponse.json();
      console.log(`📁 Executive Search: Saved CSV as ${fileRef}`);
      
      // Count unique values for verification
      const uniqueDealers = new Set(finalSubmissions.map(s => getLocationName(s.locationId)));
      const uniqueSubmissionPeriods = new Set(finalSubmissions.map(s => `${s.locationId}-${s.year}-${s.month}`));
      
      // Build analysis prompt (matching Analytics page structure)
      const prompt = `QUESTION TO ANSWER: ${message}

CRITICAL INSTRUCTIONS - READ CAREFULLY:

You are a senior financial analyst with specialized expertise in automotive accountancy and pattern recognition. You are analyzing a CSV file containing financial KPI submission data from automotive dealerships and locations. You MUST:
1. ONLY use data that exists in the provided CSV file
2. NEVER invent, estimate, or hallucinate data points
3. NEVER reference locations, dates, or values not present in the CSV
4. If data is missing or insufficient, explicitly state this limitation

KPI TIME PERIOD TYPES - CRITICAL INFORMATION:
The CSV column headers include time period type suffixes that indicate the data type:
- MTD (Monthly Values): Single calendar month or month-to-date data
- YTD (Year to Date): Cumulative from start of fiscal year to current date
- CYTD (Calendar Year to Date): Cumulative from January 1st to current date
- FYTD (Fiscal Year to Date): Cumulative from start of organization's fiscal year
- QTD (Quarter to Date): Cumulative from start of current quarter
- WTD (Week to Date): Accumulated from start of current week
- R3/L3M (Rolling 3 Months): Continuous 3-month period
- R6/L6M (Rolling 6 Months): Continuous 6-month period
- R12/TTM (Rolling 12 Months): Trailing twelve months
- PY (Prior Year): Same period from previous year
- PM (Prior Month): Immediately preceding month
- PQ (Prior Quarter): Immediately preceding quarter

IMPORTANT: When analyzing KPIs, ALWAYS consider the time period type:
- DO NOT directly compare MTD vs YTD values (different time scales)
- YTD/CYTD/FYTD values are cumulative and will be larger than MTD
- Rolling periods (R3/R6/R12) represent trends over time windows
- Prior period comparisons (PY/PM/PQ) are useful for growth analysis
- When calculating percentages or ratios, use values from the SAME time period type

ANALYSIS REQUIREMENTS:
- Base ALL findings exclusively on the provided CSV data
- Use exact location names, dates, and values from the CSV
- Apply automotive industry financial analysis best practices
- Identify dealership performance patterns and operational inefficiencies
- Recognize automotive-specific KPI trends and benchmarks
- IGNORE the quality_score field unless specifically analyzing data quality
- Focus on KPI values and business performance metrics, not data quality assessments
- If you cannot answer the question with the available data, say so explicitly

FORBIDDEN:
- Do NOT create data points not in the CSV
- Do NOT estimate missing values
- Do NOT reference locations/dates not in the file
- Do NOT make assumptions about data trends beyond what's explicitly shown
- Do NOT analyze or comment on quality_score unless specifically asked about data quality`;

      // Call Bedrock analyze/file endpoint directly (following Analytics pattern)
      const analysisResponse = await fetch(`${API_BASE_URL}/bedrock/analyze/file`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt: prompt,
          fileRef: fileRef,
          modelId: organizationModelId,
          requestType: 'analytics_submission_data',
          enableTools: true  // Enable tool use for precise counting
        })
      });

      if (!analysisResponse.ok) {
        throw new Error(`Analysis request failed: ${analysisResponse.status}`);
      }

      const data = await analysisResponse.json();
      
      if (!data.result) {
        throw new Error(data.error || 'Analysis failed');
      }
      
      console.log('✅ Executive Search: Analysis completed successfully');

      // Extract verification section from response
      const extractVerificationSection = (text: string) => {
        const startMarker = '<!-- DATA_VERIFICATION_START -->';
        const endMarker = '<!-- DATA_VERIFICATION_END -->';
        
        const startIndex = text.indexOf(startMarker);
        const endIndex = text.indexOf(endMarker);
        
        if (startIndex !== -1 && endIndex !== -1) {
          const remaining = text.substring(0, startIndex) + text.substring(endIndex + endMarker.length);
          return remaining.trim();
        }
        
        return text;
      };

      const contentWithoutVerification = extractVerificationSection(data.result);
      
      // Open report popup with the cleaned AI response
      openReport(contentWithoutVerification, message.trim());
      
      // Clear the input message after successful analysis
      setInputMessage('');
      
    } catch (err: any) {
      console.error('Executive search error:', err);
      
      let errorMessage = 'An error occurred while processing your message';
      
      // Handle network and HTTP errors first
      if (err.message?.includes('Failed to fetch') || err.message?.includes('NetworkError')) {
        errorMessage = '🌐 **Connection Error**\n\nUnable to reach the analysis service. Please check your internet connection and try again.';
      } else if (err.message?.includes('HTTP') && err.message?.includes('500')) {
        errorMessage = '⚠️ **Server Error**\n\nThe analysis service encountered an error. Please try again in a moment.';
      } else if (err.message?.includes('HTTP') && err.message?.includes('401')) {
        errorMessage = '🔒 **Authentication Error**\n\nYour session may have expired. Please refresh the page and try again.';
      } else if (err.message?.includes('HTTP') && err.message?.includes('403')) {
        errorMessage = '🚫 **Access Denied**\n\nYou do not have permission to perform this analysis. Please contact your administrator.';
      } else if (err.message?.includes('DATA_TOO_LARGE')) {
        const detailedMessage = err.message.replace('DATA_TOO_LARGE: ', '');
        errorMessage = `📊 **Dataset Too Large**\n\n${detailedMessage}`;
      } else if (err.message?.includes('Input is too long for requested model')) {
        errorMessage = '📊 **Dataset Too Large**\n\nYour question requires analyzing more data than the AI can process. Please try using the filters to narrow down your data scope, or ask more specific questions about particular time periods or locations.';
      } else if (err.message?.includes('ValidationException')) {
        errorMessage = '⚠️ **Analysis Error**\n\nThere was an issue processing your question. Please try rephrasing your question or being more specific about what you want to know.';
      } else if (err.message?.includes('context limit') || err.message?.includes('token limit')) {
        errorMessage = '📈 **Question Too Complex**\n\nYour question requires analyzing too much data simultaneously. Please try filtering your data or breaking down your question into smaller parts.';
      } else if (err.message) {
        // Show the actual error message if it's informative
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsAnalyzing(false);
    }
  };


  // Get filtered submissions based on current filter
  const getFilteredSubmissions = useCallback(() => {
    if (!allSubmissions.length) return [];
    
    let filtered = allSubmissions;
    
    // Apply filters
    if (submissionFilter.locations.length > 0) {
      // For now, we'll skip location filtering since we don't have the location mapping
      // The SubmissionFilterPanel should handle this internally
      console.log('Location filtering requested but skipped - SubmissionFilterPanel should handle this');
    }
    
    if (submissionFilter.years.length > 0) {
      filtered = filtered.filter(submission => submissionFilter.years.includes(submission.year));
    }
    
    if (submissionFilter.months.length > 0) {
      filtered = filtered.filter(submission => submissionFilter.months.includes(submission.month));
    }
    
    // Quality score filter
    if (!submissionFilter.includeAllQualityScores) {
      filtered = filtered.filter(submission => {
        const score = submission.dataQualityScore || 0;
        return score >= (organisationSettings?.validationLower || 70);
      });
    }
    
    return filtered;
  }, [allSubmissions, submissionFilter, organisationSettings]);
  
  
  // Filter handlers
  const handleApplyFilter = useCallback((filter: SubmissionFilter) => {
    setSubmissionFilter(filter);
    console.log('🔍 Executive Search: Filters applied:', filter);
  }, []);
  
  const handleClearFilter = useCallback(() => {
    setSubmissionFilter({
      locations: [],
      years: [],
      months: [],
      kpiCategories: [],
      includeAllQualityScores: true,
      intelligentFiltering: true
    });
    console.log('🗑️ Executive Search: Filters cleared');
  }, []);
  
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !isTyping) {
      e.preventDefault();
      sendMessage(inputMessage);
    }
  };
  
  const handleSendClick = () => {
    sendMessage(inputMessage);
  };

  // Calculate filtered data stats
  const filteredSubmissions = useMemo(() => getFilteredSubmissions(), [getFilteredSubmissions]);
  const totalDataPoints = useMemo(() => {
    return filteredSubmissions.reduce((sum, submission) => sum + submission.kpiData.length, 0);
  }, [filteredSubmissions]);



  // Show loading state while data is being loaded
  if (loading) {
    return (
      <div className="executive-search-fullscreen">
        <div className="executive-search-container">
            <div className="loading-interface">
              <div className="search-header">
                <h1 className="search-title">Lumina AI</h1>
                <p className="search-subtitle">
                  Loading your data for analysis...
                </p>
              </div>
              <div className="search-status">
                <div className="search-progress">
                  <div className="progress-bar"></div>
                </div>
                <p className="search-status-text">
                  Preparing your business data for AI analysis...
                </p>
              </div>
            </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="executive-search-fullscreen">
      <div className="executive-search-container">
          
          {/* Top Left Logo */}
          {organizationLogo && (
            <div className="top-left-logo">
              <img 
                src={organizationLogo} 
                alt="Organization Logo" 
                className="org-logo"
              />
            </div>
          )}

          {/* Top Right Stats */}
          <div className="top-right-stats">
            <span className="stat">
              {filteredSubmissions.length.toLocaleString()} submissions
            </span>
            <span className="stat">
              {totalDataPoints.toLocaleString()} data points
            </span>
          </div>
          
          {/* Main Content Area */}
          <div className="main-content">
            {/* Analysis Status */}
          <div className="analysis-status">
          </div>
          
          {/* Input Area */}
          <div className="conversation-input">
            <img 
              src="/LuminaAilogoPNG.png" 
              alt="Lumina AI" 
              className="input-logo"
            />
            {isAnalyzing ? (
              <div className="analyzing-status">
                <div className="analyzing-text">
                  <h3>Analyzing Your Data...</h3>
                  <p>Please wait while I process your question and generate a comprehensive report.</p>
                </div>
              </div>
            ) : (
              <p className="input-tagline">
                From data to decisions - Powering your dealer network performance
              </p>
            )}
            {error && (
              <div className="input-error">
                <span className="error-icon">⚠️</span>
                <span className="error-text">{error}</span>
                <button onClick={() => setError(null)} className="error-dismiss">×</button>
              </div>
            )}
            
            <div className="input-container">
                <textarea
                  ref={inputRef}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={modelLoading ? 'Loading AI model...' : placeholderTexts[currentPlaceholderIndex]}
                  className="message-input"
                  disabled={isAnalyzing || modelLoading}
                  rows={1}
                  style={{
                    minHeight: '44px',
                    maxHeight: '120px',
                    resize: 'none',
                    overflow: 'auto'
                  }}
                />
              <button
                onClick={handleSendClick}
                disabled={isAnalyzing || !inputMessage.trim() || modelLoading}
                className="send-button"
              >
                {isAnalyzing ? (
                  <div className="send-spinner"></div>
                ) : (
                  <span>Analyse</span>
                )}
              </button>
            </div>
            
            <div className="input-footer">
            </div>
          </div>
          </div>
          
        {/* Bottom Link - Hidden when report is active */}
        {!showReport && (
          <div className="bottom-link">
            <button 
              onClick={() => navigate('/analytics')} 
              className="dashboard-link"
            >
              Take me to the detailed Lumina AI
            </button>
          </div>
        )}
      </div>
      
      {/* Report Popup */}
      {showReport && (
        <ReportPopup
          isOpen={showReport}
          onClose={closeReport}
          analysisResult={reportData}
          fileName="Executive Search Report"
          organizationSettings={organisationSettings}
          userQuestion={reportUserQuestion}
        />
      )}
    </div>
  );
};

export default ExecutiveSearchPage;
